
const API_BASE =
  process.env.NODE_ENV === "production"
    ? process.env.REACT_APP_API_BASE_URL || ""
    : process.env.REACT_APP_API_BASE_URL || "http://localhost:5001";  


export const AUTH_COOKIE = "spendiq-auth-user";
const AUTH_COOKIE_MAX_AGE_SECONDS = 7 * 24 * 60 * 60;
let redirectingToLogin = false;

export interface User {
  public_id: string;
  email: string;
  is_active: boolean;
  firstname: string;
  lastname: string;
}

export async function validateCredentials(
  email: string,
  password: string
): Promise<User | null> {
  try {
    const res = await fetch(`${API_BASE}/auth/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({
        email: email.toLowerCase().trim(),
        password,
      }),
    });

    if (!res.ok) return null;

    const data = await res.json();

    if (data.status !== "success" || !data.data) return null;

    return {
      public_id: data.data.public_id,
      email: data.data.email,
      is_active: data.data.is_active,
      firstname: data.data.firstname,
      lastname: data.data.lastname,
    };
  } catch (error) {
    console.error("Login error:", error);
    return null;
  }
}

export function setAuthCookie(user: User) {
  clearAuthCookie();
  document.cookie = [
    `${AUTH_COOKIE}=${encodeURIComponent(JSON.stringify(user))}`,
    "Path=/",
    `Max-Age=${AUTH_COOKIE_MAX_AGE_SECONDS}`,
    "SameSite=Lax",
  ].join("; ");
}

export function refreshAuthCookie(user?: Partial<User> | null) {
  const currentUser = getAuthFromCookie();
  const refreshedUser = currentUser
    ? {
        ...currentUser,
        ...(user || {}),
      }
    : user;

  if (!refreshedUser) return;

  setAuthCookie(refreshedUser as User);
}

export function clearAuthCookie() {
  document.cookie = `${AUTH_COOKIE}=; Path=/; Max-Age=0; SameSite=Lax`;
}

export function handleAuthExpired() {
  clearAuthCookie();

  if (typeof window === "undefined") return;

  if (!redirectingToLogin && window.location.pathname !== "/login") {
    redirectingToLogin = true;
    window.location.replace("/login");
  }
}

export function getAuthFromCookie(): User | null {
  if (typeof document === "undefined") return null;
  const entry = document.cookie
    .split("; ")
    .find((row) => row.startsWith(`${AUTH_COOKIE}=`));
  if (!entry) return null;
  try {
    return JSON.parse(
      decodeURIComponent(entry.split("=").slice(1).join("="))
    ) as User;
  } catch {
    return null;
  }
}

export const auth = {
  login: async (email: string, password: string): Promise<boolean> => {
    const user = await validateCredentials(email, password);
    if (!user) return false;
    setAuthCookie(user);
    redirectingToLogin = false;
    return true;
  },

  logout: async () => {
    try {
      await fetch(`${API_BASE}/auth/logout`, {
        method: "POST",
        credentials: "include",
      });
    } finally {
      clearAuthCookie();
    }
  },

  isAuthenticated: async (): Promise<boolean> => {
    try {
      const res = await fetch(`${API_BASE}/auth/me`, {
        credentials: "include",
      });

      if (!res.ok) {
        clearAuthCookie();
        return false;
      }

      const data = await res.json();

      refreshAuthCookie(data?.user);

      return true;
    } catch (error) {
      console.error("Auth check error:", error);
      return Boolean(getAuthFromCookie());
    }
  },
};